import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Zap, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { useReveal } from "../hooks/useReveal";

export function Contact() {
  const formReveal = useReveal<HTMLDivElement>();
  const infoReveal = useReveal<HTMLDivElement>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [resultMessage, setResultMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    const formData = new FormData(event.currentTarget);

    // Add Web3Forms Access Key
    formData.append("access_key", "64fd1756-700d-43b0-8e9e-24523faeb928");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus("success");
        setResultMessage("Message sent successfully! We'll get back to you soon.");
        (event.target as HTMLFormElement).reset();
      } else {
        setSubmitStatus("error");
        setResultMessage(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setSubmitStatus("error");
      setResultMessage("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-16">
          {/* Form Side */}
          <div ref={formReveal} className="reveal lg:w-3/5">
            <div className="w-10 h-1 bg-[#2557C7] mb-8" />
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 font-primary">
              Ready to Spark a <span className="text-[#2557C7]">Connection?</span>
            </h2>
            <p className="text-slate-600 mb-10 text-lg">
              Tell us about your commercial project, service need, or electrical upgrade.
              We usually respond within 24 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Web3Forms Honeypot Spam Protection */}
              <input type="checkbox" name="botcheck" className="hidden" style={{ display: "none" }} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="John Doe"
                    className="w-full px-5 py-4 rounded-xl border-2 border-slate-100 focus:border-[#2557C7] transition-colors outline-none font-medium"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full px-5 py-4 rounded-xl border-2 border-slate-100 focus:border-[#2557C7] transition-colors outline-none font-medium"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="phone" className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    placeholder="(504) 000-0000"
                    className="w-full px-5 py-4 rounded-xl border-2 border-slate-100 focus:border-[#2557C7] transition-colors outline-none font-medium"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="service" className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                    Service Needed
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    className="w-full px-5 py-4 rounded-xl border-2 border-slate-100 focus:border-[#2557C7] transition-colors outline-none font-medium bg-white"
                  >
                    <option value="">Select a service</option>
                    <option value="Commercial">Commercial Project</option>
                    <option value="Service Call">Commercial Service Call</option>
                    <option value="Panel Upgrade">Panel Upgrade</option>
                    <option value="Generator">Generator Install</option>
                    <option value="EV Charging">EV Charging Station</option>
                    <option value="Residential">Residential Service</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                  Project Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  placeholder="Tell us about the property, project scope, timeline, and electrical needs..."
                  className="w-full px-5 py-4 rounded-xl border-2 border-slate-100 focus:border-[#2557C7] transition-colors outline-none font-medium resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto rounded-lg bg-[#2557C7] px-7 py-3.5 text-base font-bold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-[#2557C7]/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    Sending
                  </>
                ) : (
                  "Request Estimate"
                )}
              </button>

              {/* Status Messages */}
              {submitStatus === "success" && (
                <div className="p-4 rounded-xl bg-green-50 text-green-700 flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-bold">{resultMessage}</span>
                </div>
              )}
              {submitStatus === "error" && (
                <div className="p-4 rounded-xl bg-red-50 text-red-700 flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-bold">{resultMessage}</span>
                </div>
              )}
            </form>
          </div>

          {/* Info Side */}
          <div ref={infoReveal} className="reveal lg:w-2/5 space-y-10">
            <div>
              <h3 className="text-xl font-black text-slate-900 mb-6 uppercase tracking-widest">Connect Directly</h3>
              <div className="space-y-6">
                <a href="tel:5042872940" className="flex items-center gap-5 group">
                  <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center group-hover:bg-[#2557C7] transition-colors">
                    <Phone className="h-6 w-6 text-[#2557C7] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">Call or Text</p>
                    <p className="text-xl font-black text-slate-900">(504) 287-2940</p>
                  </div>
                </a>

                <a href="mailto:Contact@power-one-electric.com" className="flex items-center gap-5 group">
                  <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center group-hover:bg-[#2557C7] transition-colors">
                    <Mail className="h-6 w-6 text-[#2557C7] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">Email Us</p>
                    <p className="text-xl font-black text-slate-900 line-clamp-1">Contact@power-one-electric.com</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="p-8 bg-slate-900 rounded-[32px] text-white space-y-6 relative overflow-hidden shadow-2xl">
              <Zap className="absolute -top-6 -right-6 h-32 w-32 text-[#2557C7] opacity-10" />
              <h4 className="text-lg font-black uppercase tracking-widest relative z-10">Working Hours</h4>
              <div className="space-y-4 relative z-10">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-[#2557C7]" />
                  <span className="text-slate-400 font-bold ml-2">Mon - Fri:</span>
                  <span className="ml-auto font-black italic">7:00 AM - 5:00 PM</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-slate-400 opacity-50" />
                  <span className="text-slate-400 font-bold ml-2">Sat - Sun:</span>
                  <span className="ml-auto font-black italic text-[#2557C7]">Emergency Only</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-[#2557C7]" />
                <span className="font-black text-slate-900 uppercase tracking-widest text-sm text-[11px]">Primary Service Area</span>
              </div>
              <p className="text-slate-600 font-medium">
                Proudly serving New Orleans, Metairie, Kenner, Gretna, and surrounding South Louisiana parishes.
              </p>
            </div>
          </div>
        </div>

        {/* Google Maps Embed */}
        <div className="mt-24 rounded-[40px] overflow-hidden shadow-2xl border-8 border-slate-50 relative group">
          <div className="absolute inset-x-0 top-0 h-1 bg-linear-to-r from-transparent via-[#2557C7] to-transparent opacity-50" />
          <iframe
            src="https://maps.google.com/maps?q=918%2025th%20St%2C%20Kenner%2C%20LA%2070062&t=&z=14&ie=UTF8&output=embed"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen={true}
            loading="lazy"
            className="grayscale-20 group-hover:grayscale-0 transition-all duration-700"
            title="Power One Electric Location"
          ></iframe>
        </div>
      </div>
    </section>
  );
}
